// MCHD Room Scheduler - Security Dashboard Page
// Main page for security monitoring and management

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Group,
  Card,
  Stack,
  Badge,
  Button,
  Box,
  Alert,
  Grid,
  Tabs,
} from '@mantine/core';
import {
  IconShield,
  IconAlertCircle,
  IconChartBar,
  IconClock,
  IconUsers,
  IconBan,
  IconLockOpen,
} from '@tabler/icons-react';
import { useSecurityMonitoring } from '../../../hooks/admin/useSecurityMonitoring';
import DynamicRefreshButton from '../../../components/atoms/DynamicRefreshButton';
import { FailedAttemptsTable } from '../../../components/organisms/SecurityDashboard/FailedAttemptsTable';
import { SecurityStatsCard } from '../../../components/organisms/SecurityDashboard/SecurityStatsCard';
import { SecurityCardSkeleton } from '../../../components/molecules/SecurityCardSkeleton';
import { TopOffendingSkeleton } from '../../../components/molecules/TopOffendingSkeleton';
import { TopOffendingIPs } from '../../../components/molecules/TopOffendingIPs';
import { TopOffendingEmails } from '../../../components/molecules/TopOffendingEmails';
import { AttemptDetailModal } from '../../../components/molecules/AttemptDetailModal';
import { FailedAttempt } from '../../../api/endpoints/auth';

/**
 * Security dashboard page for administrators
 * @returns JSX element
 */
export function SecurityDashboard(): JSX.Element {
  // Hooks
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [selectedAttempt, setSelectedAttempt] = useState<FailedAttempt | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const {
    failedAttempts,
    isLoading,
    error,
    unlockAccount,
    unlockIP,
    isUnlockingAccount,
    isUnlockingIP,
    metrics,
    securityScore,
    securityLevel,
    securityInsights,
    refetchAll,
  } = useSecurityMonitoring();

  const handleUnlockAccount = (email: string) => {
    unlockAccount(email);
  };

  const handleUnlockIP = (ipAddress: string) => {
    unlockIP(ipAddress);
  };

  const handleViewAttempt = (attempt: FailedAttempt) => {
    setSelectedAttempt(attempt);
    setDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedAttempt(null);
  };

  const handleRefresh = () => {
    refetchAll();
  };

  // Use security score and level from the hook instead of calculating locally
  const securityStatus = {
    status: securityLevel.level,
    color: securityLevel.color,
  };

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Security Data"
          color="red"
          variant="light"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={1} mb="xs">
              Security Dashboard
            </Title>
            <Text size="lg" c="dimmed">
              Monitor security threats and manage system access
            </Text>
          </Box>
          <Group gap="sm">
            <DynamicRefreshButton
              variant="outline"
              onRefresh={handleRefresh}
              isLoading={isLoading}
              size="sm"
              color="blue"
            />
            <Badge
              color={securityStatus.color}
              variant="light"
              size="lg"
              leftSection={<IconShield size={16} />}
            >
              {securityStatus.status}
            </Badge>
          </Group>
        </Group>

        {/* Security Overview Cards */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            {isLoading ? (
              <SecurityCardSkeleton />
            ) : (
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500} c="dimmed">
                    Security Score
                  </Text>
                  <IconShield size={20} color={`var(--mantine-color-${securityStatus.color}-6)`} />
                </Group>
                <Text size="xl" fw={700} c={securityStatus.color}>
                  {securityScore}
                </Text>
                <Text size="xs" c="dimmed">
                  {securityStatus.status} security level
                </Text>
              </Card>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            {isLoading ? (
              <SecurityCardSkeleton />
            ) : (
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500} c="dimmed">
                    Failed Attempts
                  </Text>
                  <IconAlertCircle size={20} color="var(--mantine-color-red-6)" />
                </Group>
                <Text size="xl" fw={700} c="red">
                  {metrics.totalAttempts}
                </Text>
                <Text size="xs" c="dimmed">
                  {metrics.unresolvedAttempts} unresolved
                </Text>
              </Card>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            {isLoading ? (
              <SecurityCardSkeleton />
            ) : (
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500} c="dimmed">
                    Locked Accounts
                  </Text>
                  <IconBan size={20} color="var(--mantine-color-orange-6)" />
                </Group>
                <Text size="xl" fw={700} c="orange">
                  {metrics.lockedAccounts}
                </Text>
                <Text size="xs" c="dimmed">
                  Accounts requiring unlock
                </Text>
              </Card>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            {isLoading ? (
              <SecurityCardSkeleton />
            ) : (
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500} c="dimmed">
                    Active Sessions
                  </Text>
                  <IconClock size={20} color="var(--mantine-color-green-6)" />
                </Group>
                <Text size="xl" fw={700} c="green">
                  {metrics.activeSessions}
                </Text>
                <Text size="xs" c="dimmed">
                  Currently online
                </Text>
              </Card>
            )}
          </Grid.Col>
        </Grid>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="attempts" leftSection={<IconAlertCircle size={16} />}>
              Failed Attempts
            </Tabs.Tab>
            <Tabs.Tab value="accounts" leftSection={<IconUsers size={16} />}>
              Account Management
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <SecurityStatsCard 
                  stats={{
                    totalUsers: metrics.totalUsers,
                    activeUsers: metrics.activeUsers,
                    failedAttempts: metrics.totalAttempts,
                    lockedAccounts: metrics.lockedAccounts,
                    activeSessions: metrics.activeSessions,
                    securityScore: metrics.securityScore,
                  }} 
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text size="lg" fw={600} mb="md">
                    Recent Activity
                  </Text>
                  
                  <Stack gap="md">
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>Recent Failed Attempts</Text>
                        <Badge color="red" variant="light">
                          {metrics.recentAttempts}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Last hour
                      </Text>
                    </Box>
                    
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>Unique IP Addresses</Text>
                        <Badge color="blue" variant="light">
                          {metrics.uniqueIPs}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Involved in failed attempts
                      </Text>
                    </Box>
                    
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>Unique Email Addresses</Text>
                        <Badge color="orange" variant="light">
                          {metrics.uniqueEmails}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Targeted in attacks
                      </Text>
                    </Box>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Security Overview Section */}
            <Grid mt="xl">
              <Grid.Col span={12}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text size="lg" fw={600} mb="md">
                    Security Overview
                  </Text>
                  
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>Total Users</Text>
                          <Text size="sm" fw={600}>{metrics.totalUsers}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          All registered users
                        </Text>
                      </Box>
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>Active Users</Text>
                          <Text size="sm" fw={600} c="green">{metrics.activeUsers}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Currently active
                        </Text>
                      </Box>
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>Failed Attempts</Text>
                          <Text size="sm" fw={600} c="red">{metrics.totalAttempts}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Total failed login attempts
                        </Text>
                      </Box>
                    </Grid.Col>
                    
                    <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>Locked Accounts</Text>
                          <Text size="sm" fw={600} c="orange">{metrics.lockedAccounts}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Requiring unlock
                        </Text>
                      </Box>
                    </Grid.Col>
                  </Grid>
                  
                  <Box mt="md" p="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 'var(--mantine-radius-sm)' }}>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" fw={500}>Active Sessions</Text>
                      <Text size="sm" fw={600} c="green">{metrics.activeSessions}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Currently online
                    </Text>
                  </Box>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Security Insights Section */}
            {(securityInsights.insights.length > 0 || securityInsights.recommendations.length > 0) && (
              <Grid mt="xl">
                <Grid.Col span={12}>
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Text size="lg" fw={600} mb="md">
                      Security Insights & Recommendations
                    </Text>
                    
                    <Stack gap="md">
                      {/* Security Insights */}
                      {securityInsights.insights.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} mb="sm" c="blue">
                            Current Security Alerts
                          </Text>
                          <Stack gap="xs">
                            {securityInsights.insights.map((insight, index) => (
                              <Alert
                                key={index}
                                color={insight.type === 'critical' ? 'red' : insight.type === 'warning' ? 'yellow' : 'blue'}
                                variant="light"
                                icon={<IconAlertCircle size={16} />}
                              >
                                <Text size="sm" fw={500} mb="xs">
                                  {insight.message}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {insight.action}
                                </Text>
                              </Alert>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      
                      {/* Security Recommendations */}
                      {securityInsights.recommendations.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} mb="sm" c="green">
                            Recommendations
                          </Text>
                          <Stack gap="xs">
                            {securityInsights.recommendations.map((recommendation, index) => (
                              <Box
                                key={index}
                                p="sm"
                                style={{
                                  backgroundColor: 'var(--mantine-color-green-0)',
                                  borderRadius: 'var(--mantine-radius-sm)',
                                  border: '1px solid var(--mantine-color-green-2)',
                                }}
                              >
                                <Text size="sm" c="green">
                                  • {recommendation}
                                </Text>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            )}

            {/* Top Offending Data Section */}
            <Grid mt="xl">
              <Grid.Col span={{ base: 12, md: 6 }}>
                {isLoading ? (
                  <TopOffendingSkeleton title="Top Offending IPs" />
                ) : (
                  <TopOffendingIPs data={metrics.topOffendingIPs} />
                )}
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                {isLoading ? (
                  <TopOffendingSkeleton title="Top Offending Emails" />
                ) : (
                  <TopOffendingEmails data={metrics.topOffendingEmails} />
                )}
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="attempts" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Text size="lg" fw={600}>
                  Failed Login Attempts
                </Text>
                <Group gap="sm">
                  <Text size="sm" c="dimmed">
                    {metrics.totalAttempts} total attempts
                  </Text>
                </Group>
              </Group>

              <FailedAttemptsTable
                attempts={failedAttempts}
                isLoading={isLoading}
                onUnlockAccount={handleUnlockAccount}
                onUnlockIP={handleUnlockIP}
                onView={handleViewAttempt}
                onRefresh={handleRefresh}
                metrics={metrics}
              />
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="accounts" pt="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text size="lg" fw={600} mb="md">
                    Account Status
                  </Text>
                  
                  <Stack gap="md">
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>Total Users</Text>
                        <Text size="sm" fw={600}>{metrics.totalUsers || 0}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        All registered users
                      </Text>
                    </Box>
                    
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>Active Users</Text>
                        <Text size="sm" fw={600} c="green">{metrics.activeUsers || 0}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Currently active
                      </Text>
                    </Box>
                    
                    <Box>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={500}>Locked Accounts</Text>
                        <Text size="sm" fw={600} c="red">{metrics.lockedAccounts || 0}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Requiring unlock
                      </Text>
                    </Box>
                  </Stack>
                </Card>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text size="lg" fw={600} mb="md">
                    Quick Actions
                  </Text>
                  
                  <Stack gap="sm">
                    <Button
                      variant="outline"
                      leftSection={<IconLockOpen size={16} />}
                      onClick={() => setActiveTab('attempts')}
                      disabled={metrics.unresolvedAttempts === 0}
                    >
                      Manage Failed Attempts
                      {metrics.unresolvedAttempts > 0 && (
                        <Badge color="red" size="xs" ml="xs">
                          {metrics.unresolvedAttempts}
                        </Badge>
                      )}
                    </Button>
                    
                    <Button
                      variant="outline"
                      leftSection={<IconUsers size={16} />}
                      onClick={() => navigate('/admin/users')}
                    >
                      View All Users
                    </Button>
                    
                    <DynamicRefreshButton
                      variant="outline"
                      onRefresh={handleRefresh}
                      isLoading={isLoading}
                      size="sm"
                      color="blue"
                      children="Refresh Security Data"
                    />
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Attempt Detail Modal */}
      <AttemptDetailModal
        attempt={selectedAttempt}
        opened={detailModalOpen}
        onClose={handleCloseModal}
        onUnlockAccount={handleUnlockAccount}
        onUnlockIP={handleUnlockIP}
        onRefresh={handleRefresh}
        isUnlockingAccount={isUnlockingAccount}
        isUnlockingIP={isUnlockingIP}
      />
    </Container>
  );
}
